/**
 * Job status pill (Phase 65 / BLU-594 PR3b). One semantic colour per state, no
 * new accents: purple = in-motion (planning/running), amber = needs the
 * operator (awaiting_approval), emerald = completed, red = failed, muted =
 * queued/cancelled. Always paired with text + (for live states) a spinner.
 */
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProductSyncJobStatus } from '../../lib/apply-types';

const STYLE: Record<ProductSyncJobStatus, { cls: string; spin: boolean }> = {
  queued: { cls: 'bg-muted text-muted-foreground ring-border', spin: true },
  planning: { cls: 'bg-threadheads-purple/10 text-threadheads-purple ring-threadheads-purple/30', spin: true },
  awaiting_approval: { cls: 'bg-amber-100 text-amber-800 ring-amber-300/80', spin: false },
  running: { cls: 'bg-threadheads-purple/10 text-threadheads-purple ring-threadheads-purple/30', spin: true },
  completed: { cls: 'bg-emerald-100 text-emerald-800 ring-emerald-300/80', spin: false },
  failed: { cls: 'bg-red-100 text-red-800 ring-red-300/80', spin: false },
  cancelled: { cls: 'bg-muted text-muted-foreground ring-border', spin: false },
};

export function JobStatusPill({ status, size = 'md' }: { status: ProductSyncJobStatus; size?: 'md' | 'lg' }) {
  const { t } = useTranslation('productSync');
  const style = STYLE[status];
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center gap-1.5 rounded-md font-semibold uppercase tracking-wide ring-1',
        size === 'lg' ? 'px-3 py-1 text-xs' : 'px-2.5 py-0.5 text-[10px]',
        style.cls,
      )}
    >
      {style.spin && <Loader2 className={cn('animate-spin', size === 'lg' ? 'h-3.5 w-3.5' : 'h-3 w-3')} aria-hidden="true" />}
      {t(`job.status.${status}`)}
    </span>
  );
}
